import React, { useEffect, useRef } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";

const Match = ({
  matchSelections,
  currentActivity,
  submitted,
  handleSelectChange,
  shuffledValues,
  shuffledNames,
}) => {
  const rationaleRef = useRef();
  console.log(submitted);
  useEffect(() => {
    if (submitted && rationaleRef.current) {
      setTimeout(() => {
        rationaleRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100); // Adjust delay if needed
    }
  }, [submitted]);
  let combinedRationale = [];
  let isQuestionCorrect = false;

  if (submitted) {
    isQuestionCorrect = currentActivity.Options.every((option) => {
      // Find the selected value in matchSelections based on the value
      const selectedValue = Object.values(matchSelections).find(
        (value) => value === option.Value
      );
      return selectedValue === option.Value; // Match selected answer with the correct one
    });
    // Combine all rationales for the current question
    combinedRationale = currentActivity.Options.map((option) => {
      return option.Rationale?.Text || null;
    })
      .filter((text) => text) // Remove null or empty texts
      .join(" "); // Combine them into a single string
  }
  // Check if the entire question is correct

  return (
    <>
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            marginTop: "10px",
          }}
        >
          {shuffledNames.map((name, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                minHeight: "56px", // Ensures consistent row height
              }}
            >
              {/* Check if name is a URL and display image */}
              {/^https?:\/\//.test(name) ? (
                <img
                  src={name}
                  alt={`Image ${index}`}
                  style={{
                    maxWidth: "100px", // Set a max width for the image
                    maxHeight: "50px", // Set a max height
                    objectFit: "contain", // Ensure the image scales properly
                  }}
                />
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    flex: 1,
                    textAlign: "left",
                    wordBreak: "break-word", // Prevents overflow for long names
                  }}
                >
                  {name}
                </Typography>
              )}

              {/* Dropdown on the right */}
              <FormControl
                variant="outlined"
                sx={{
                  minWidth: 200,
                  position: "relative", // Ensures dropdown aligns well with the parent box
                }}
              >
                <InputLabel id={`select-label-${index}`}>Select</InputLabel>
                <Select
                  labelId={`select-label-${index}`}
                  value={matchSelections[index] || ""}
                  onChange={(e) => handleSelectChange(index, e.target.value)}
                  label="Select"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // Limits dropdown height to avoid overflow
                        overflowY: "auto", // Adds smooth scrolling for large dropdowns
                      },
                    },
                    disableScrollLock: true, // Prevents locking of the parent scrollbar
                  }}
                  disabled={submitted} // Disable the Select component if submitted[activityId] is true
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {shuffledValues.map((value, valueIndex) => (
                    <MenuItem key={valueIndex} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ))}
        </Box>
      </Box>
      {submitted && (
        <Box
          ref={rationaleRef}
          sx={{
            textAlign: "center",
            marginTop: 4,
            padding: 2,
            borderRadius: 2,
            backgroundColor: isQuestionCorrect ? "#e8f5e9" : "#ffebee", // Green if correct, red if incorrect
          }}
        >
          {/* Icon and message */}
          {isQuestionCorrect ? (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "green" }} />
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "1rem",
                  color: "green",
                  marginTop: 1,
                }}
              >
                Correct!
              </Typography>
            </>
          ) : (
            <>
              <CancelIcon sx={{ fontSize: 40, color: "red" }} />
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.25rem",
                  color: "red",
                  marginTop: 1,
                }}
              >
                Incorrect!
              </Typography>
            </>
          )}

          {/* Rationale display */}
          {combinedRationale?.trim() ? (
            <Typography
              sx={{
                marginTop: 1,
                fontSize: "0.75rem",
                color: "text.secondary",
                padding: "8px",
                margin: "8px 0",
              }}
            >
              {combinedRationale}
            </Typography>
          ) : (
            <>
              {/* Look for rationale from options if combinedRationale is not present */}
              {Object.entries(currentActivity?.Options || {}).some(
                ([, optionsArray]) =>
                  optionsArray.some(
                    (option) =>
                      option.Rationale?.Text?.trim() ||
                      option.Rationale?.ContentId?.trim()
                  )
              ) ? (
                Object.entries(currentActivity.Options).map(
                  ([optionIndex, optionsArray]) =>
                    optionsArray.map((option, i) => {
                      const rationaleText = option.Rationale?.Text?.trim();
                      const contentId = option.Rationale?.ContentId?.trim();

                      return (
                        (rationaleText || contentId) && (
                          <Box
                            key={`rationale-${optionIndex}-${i}`}
                            sx={{
                              marginTop: 1,
                              fontSize: "0.75rem",
                              color: "text.secondary",
                              padding: "8px",
                              borderTop: "1px solid #ddd",
                            }}
                          >
                            <Typography>
                              <strong>
                                Rationale for Option {optionIndex}:
                              </strong>{" "}
                              {rationaleText
                                ? rationaleText
                                : `Refer to content ID: ${contentId}`}
                              {/* You can replace above with a component like: <LessonRenderer id={contentId} /> */}
                            </Typography>
                          </Box>
                        )
                      );
                    })
                )
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontStyle: "italic",
                    color: "#999",
                    marginTop: 1,
                  }}
                >
                  Rationale: Not available
                </Typography>
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default Match;
